import clsx from 'clsx'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import NewsHighlights from '../../../../UI/NewsHighlights'
import CompanyProgress from '../../../../UI/CompanyProgress'
import CompanySteps from '../../common/CompanySteps'
import NotificationsPreview from '../../common/NotificationsPreview'
import { toast } from 'react-toastify'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { downloadFile, mapCompanyTypeToName } from '../../../../shared/helpers'
import { openApprovalRequirements } from '../../../../shared/approvalDialog'
import { useCompanyStepFocus } from '../../../../shared/companyStepLink'

import { updateCompanyInfo } from '../../../../redux/ducks/user'

import styles from './style.module.scss'
import { useAvatarUrl } from '../../../../shared/useAvatarUrls'

/*
 * Dashboard der Schüler:innen.
 *
 * Darunter standen bis 08.08.2026 fünf Kacheln: Mitarbeiter:innen, weitere
 * Betreuungslehrkräfte, Wirtschaftsexpert:innen, Risikoanalyse und
 * Businessplan. Sie zeigten Namenskreise und Zahlen — also das, was die
 * Menüpunkte daneben vollständig zeigen — und beantworteten die eine Frage
 * nicht, mit der man aufs Dashboard kommt: Was ist als Nächstes zu tun? Das
 * stand allein im Fenster hinter „Zur Genehmigung einreichen", und damit erst
 * dort, wo es zu spät ist.
 *
 * An ihrer Stelle stehen drei Spalten:
 *
 *  1. was zu tun ist (`views/common/CompanySteps`) — vor der Aktivierung die
 *     Voraussetzungen, danach die Schritte bis zum Abschluss,
 *  2. die jüngsten Mitteilungen mit Verweis auf den Menüpunkt,
 *  3. Veranstaltungen & Neuigkeiten mit Verweis auf die Vollansicht.
 *
 * Der Kopf und der Fortschrittsbalken bleiben, wie sie waren.
 */
function Dashboard(props) {
	const { t } = useTranslation()
	const [dashboard, setDashboard] = useState({
		news: [],
		lastExperts: [],
		employees: {
			count: 0,
			rows: []
		},
		experts: {
			count: 0,
			rows: []
		},
		supervisingTeacher: {},
		teachers: {
			count: 0,
			rows: []
		},
		riskAnalysis: null,
		businessPlan: null,
		shareholders: [],
		activationsStatus: {},
		lastReports: [],
		finalReport: null
	})

	const [isLoading, setLoading] = useState(false)

	// Balken und Checkliste zeigen denselben Ablauf. Welcher Schritt gerade in
	// beiden hervorgehoben ist, gehört deshalb keiner der beiden Spalten allein
	// (siehe shared/companyStepLink).
	const { focusId, focusStep } = useCompanyStepFocus()

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein
	// <img src> könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarSrc = useAvatarUrl(dashboard.supervisingTeacher.avatarHashName)

	const ladeDashboard = () =>
		http.get(TApi.STUDENT_GET_DASHBOARD).then(({ data }) => {
			setDashboard(prev => ({ ...prev, ...data }))
			return data
		})

	const handleAddBusinessPlan = evt => {
		setLoading(true)
		const form = new FormData()

		form.append('document', evt.target.files[0])

		http.post(TApi.STUDENT_POST_UPLOAD_BUSINESS_PLAN, form)
			.then(({ data }) => {
				setDashboard(prev => ({
					...prev,
					businessPlan: data,
					activationsStatus: {
						...prev.activationsStatus,
						businessPlan: true
					}
				}))
				toast.success(t('Business Plan uploaded successfully'))
			})
			.finally(() => setLoading(false))
	}

	const handleDeleteBusinessPlan = evt => {
		evt.preventDefault()
		evt.stopPropagation()
		setLoading(true)

		http.delete(TApi.STUDENT_POST_UPLOAD_BUSINESS_PLAN)
			.then(() => {
				setDashboard(prev => ({
					...prev,
					businessPlan: null,
					activationsStatus: {
						...prev.activationsStatus,
						businessPlan: false
					}
				}))
				toast.success(t('Business Plan removed successfully'))
			})
			.finally(() => setLoading(false))
	}

	const handleDeleteFinalReport = evt => {
		evt.preventDefault()
		evt.stopPropagation()
		setLoading(true)

		http.delete(TApi.STUDENT_POST_UPLOAD_FINAL_REPORT)
			.then(() => {
				setDashboard(prev => ({
					...prev,
					finalReport: null,
					activationsStatus: {
						...prev.activationsStatus,
						finalReport: false
					}
				}))
				toast.success(t('Final report removed successfully'))
			})
			.finally(() => setLoading(false))
	}

	const handleAddFinalReport = evt => {
		setLoading(true)
		const form = new FormData()

		form.append('document', evt.target.files[0])

		http.post(TApi.STUDENT_POST_UPLOAD_FINAL_REPORT, form, { timeout: 180000 })
			.then(() => {
				toast.success(t('Final Report uploaded successfully'))
				// Frischen Stand laden, damit die Zeile direkt auf "herunterladen" wechselt
				// (der Upload-Endpoint liefert keinen verwertbaren Body zurück).
				return ladeDashboard()
			})
			.finally(() => setLoading(false))
	}

	const handleDownloadDocument = datei => {
		if (!datei?.originalName) return

		http.get(TApi.GET_FILES_DOCUMENT(datei.name), {
			headers: {
				'Content-Type': 'application/pdf'
			},
			responseType: 'blob'
		}).then(({ data }) => downloadFile(datei.originalName, data))
	}

	const handleSubmitReview = async () => {
		/*
		 * Erst den Stand holen, dann die Liste zeigen. Er kann seit dem Laden
		 * der Seite gekippt sein — jemand tritt aus, eine Einladung geht hinaus,
		 * ein zweites Fenster steht offen. Das Fenster hätte dann Häkchen
		 * gezeigt, die der Server nicht mehr bestätigt.
		 */
		setLoading(true)
		const aktuell = await ladeDashboard().catch(() => null)
		setLoading(false)

		// Dieselbe Liste, die die Administration unter Einstellungen →
		// Checkliste & Voraussetzungen zu sehen bekommt. Das Fenster selbst
		// steht in shared/approvalDialog — die Mitteilung „Bereit zum Einreichen"
		// öffnet dasselbe.
		openApprovalRequirements({
			options: props.options,
			activationsStatus: aktuell?.activationsStatus || dashboard.activationsStatus,
			onSubmitted: props.updateCompanyInfo,
			/*
			 * Der Server prüft die Voraussetzungen seit 21.08.2026 noch einmal
			 * selbst und nennt in seiner Meldung, was offen ist — angezeigt wird
			 * sie vom Fehlerweg in shared/http. Hier bleibt das Nachladen: Die
			 * Checkliste daneben soll danach dasselbe sagen wie der Server.
			 */
			onSubmitFailed: () => ladeDashboard().catch(() => {})
		})
	}

	useEffect(() => {
		if (!props.isSignInMode && props.isAuth && props.email) {
			ladeDashboard()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isAuth, props.isSignInMode, props.email])

	return (
		<div className={styles.root}>
			<div className={clsx('container', styles.header)}>
				<div>
					<h2 className={styles.companyTitle}>{props.company.name}</h2>
					<p className={styles.companyType}>{mapCompanyTypeToName(props.company.type)}</p>
					<span className={styles.branch}>{t(props.company.branch)}</span>
				</div>
				<div className={styles.statusWrapper}>
					{props.company.isActivated ? (
						<span className={styles.tag}>{t('Company approved')}</span>
					) : (
						<span className={styles.tagCompanyStatus}>
							<Icon name="danger" size={{ w: 24, h: 24 }} color="#fff" />
							<span className={styles.tagStatusText}>
								{props.company.isNeedReview
									? t('Company submitted for approval')
									: t('Company not yet approved')}
							</span>
						</span>
					)}
					{!props.company.isNeedReview && !props.company.isActivated && !props.isTeacherStudentView && (
						<Btn
							disabled={isLoading}
							onClick={handleSubmitReview}
							className={styles.btnSubmitReview}
							theme="minimal"
						>
							{t('Submit for approval')}
						</Btn>
					)}
				</div>
				<div className={styles.user}>
					{dashboard.supervisingTeacher?.firstName ? (
						<Avatar
							className={styles.userAvatar}
							src={avatarSrc}
							size={32}
							round
							name={`${dashboard.supervisingTeacher?.firstName} ${dashboard.supervisingTeacher?.lastName}`}
						/>
					) : (
						''
					)}

					<div className={styles.userInfo}>
						<span className={styles.userFullName}>
							{dashboard.supervisingTeacher?.firstName} {dashboard.supervisingTeacher?.lastName}
						</span>
						<span className={styles.userType}>{t('Supervision Teacher')}</span>
					</div>
					{!props.isTeacherStudentView && (
						<Btn
							disabled={isLoading}
							className={styles.userProfileAdd}
							icon="plus"
							iconColor="#22404D"
							iconSize={{ w: 12, h: 12 }}
							theme="white"
							onClick={() => eventEmitter.emit(types.openAddTeacherModal)}
						/>
					)}
				</div>
			</div>
			<div className={clsx('container', styles.progress)}>
				<CompanyProgress
					companyType={props.company.type}
					activationsStatus={dashboard.activationsStatus}
					isActivated={!!props.company?.isActivated}
					isNeedReview={!!props.company.isNeedReview}
					reportsDeadline={props.options?.reportsDeadline}
					focusId={focusId}
					onFocusStep={focusStep}
				/>
			</div>
			<div className={clsx('container', styles.columns)}>
				<CompanySteps
					companyType={props.company.type}
					isActivated={props.company.isActivated}
					isNeedReview={props.company.isNeedReview}
					activationsStatus={dashboard.activationsStatus}
					businessPlan={dashboard.businessPlan}
					finalReport={dashboard.finalReport}
					isLoading={isLoading}
					focusId={focusId}
					onFocusStep={focusStep}
					// In der Schüler:innen-Ansicht der Lehrkraft wird nur gelesen —
					// dieselbe Regel wie bei den Formularen (siehe UI/TextField).
					readOnly={props.isTeacherStudentView}
					actions={{
						addEmployee: () => eventEmitter.emit(types.openAddEmployeeModal, ladeDashboard),
						uploadBusinessPlan: handleAddBusinessPlan,
						deleteBusinessPlan: handleDeleteBusinessPlan,
						uploadFinalReport: handleAddFinalReport,
						deleteFinalReport: handleDeleteFinalReport,
						downloadBusinessPlan: handleDownloadDocument,
						downloadFinalReport: handleDownloadDocument
					}}
				/>

				<NotificationsPreview embedded />

				<NewsHighlights single items={dashboard.news} toFullList={TRoutes.NEWS} />
			</div>
		</div>
	)
}

Dashboard.propTypes = {
	company: PropTypes.shape({
		isActivated: PropTypes.bool.isRequired,
		isNeedReview: PropTypes.bool.isRequired,
		name: PropTypes.string.isRequired,
		type: PropTypes.string.isRequired,
		branch: PropTypes.string.isRequired
	}).isRequired,

	updateCompanyInfo: PropTypes.func.isRequired,

	isAuth: PropTypes.bool.isRequired,
	isSignInMode: PropTypes.bool.isRequired,
	email: PropTypes.bool.isRequired,
	options: PropTypes.object.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	company: {
		isActivated: !!state.user.profile.EmploymentCompany?.isActive,
		isNeedReview: !!state.user.profile.EmploymentCompany?.isNeedReview,
		name: state.user.profile.EmploymentCompany?.name || '',
		type: state.user.profile.EmploymentCompany?.companyType || '',
		branch: state.user.profile.EmploymentCompany?.branch || '',
		emailVerified: state.user.profile.EmploymentCompany?.emailVerified || false
	},
	isAuth: state.user.isAuth,
	isSignInMode: state.user.isSignInMode,
	email: state.user.profile.email,
	options: state.options,
	profile: state.user.profile.EmploymentCompany?.isActive,
	isTeacherStudentView: state.user.isTeacherStudentView
})

const mapDispatchToProps = dispatch => ({
	updateCompanyInfo: data => dispatch(updateCompanyInfo(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
